import type { InsuranceVerdict } from '@/hooks/useInsuranceVerdicts'
import {
  Menu,
  Modal,
  Popover,
  Select,
  Skeleton,
  Tooltip,
  type FloatingPosition,
} from '@mantine/core'
import { useLayoutEffect, useRef, useState } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { useNavigate } from 'react-router-dom'
import { useMedplum } from '@medplum/react'
import { formatHumanName } from '@medplum/core'
import { showNotification } from '@mantine/notifications'
import {
  IconArrowsExchange,
  IconBuildingHospital,
  IconCalendarEvent,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconCreditCard,
  IconFileDescription,
  IconFilePlus,
  IconVideoFilled,
  IconHistory,
  IconMail,
  IconPhone,
  IconSend,
  IconShieldCheck,
  IconShieldFilled,
  IconSparkles,
  IconTrash,
  IconUser,
  IconWallet,
  IconX,
  IconExclamationMark,
} from '@tabler/icons-react'
import {
  APPOINTMENT_STATUS_CHIP,
  APPOINTMENT_STATUS_ICON,
  APPOINTMENT_STATUS_OPTIONS,
  INSURANCE_STATUS_CHIP,
  fmtTime,
  type AppointmentStatusValue,
  type CalendarEvent,
} from './scheduleCalendar.tokens'
import { type PreviewPopover } from '../hooks/usePreviewPopover'
import type { Appointment, CoverageEligibilityResponse } from '@medplum/fhirtypes'
import { calculateAge, formatDateTime } from '@/utils.ts'
import { getCoveragePlanSnapshot, getInsuranceDisplayData } from '@/utils/coverageUtils'
import {
  PAYER_ELIGIBILITY_LABELS,
  clinicDecisionLabel,
  payerEligibilityColor,
} from '@/utils/insuranceVerdict'
import { isSelfPayAppointment } from '@/utils/selfPay'
import { PLAN_TYPES_BY_CATEGORY } from '@behuman/core-contract'
import { CoverageEligibilityOutcomePanel, VisitCoPayPanel } from '@/components/ui'
import { INSURANCE_PLAN_TYPE_SYSTEM } from '@/model/middleware-types'
import {
  EligibilityDecisionModal,
  type EligibilityDecisionMode,
} from '@/components/ui/EligibilityDecisionModal'
import {
  buildInsuranceOrgDecisionQuestionnaireResponse,
  resolveCurrentOrganizationId,
} from '@/utils/insuranceQuestionnaire'
import { useIsManager } from '@/hooks/useIsManager'
import { useAppointmentInsurance } from '../hooks/useAppointmentInsurance'
import { TransferAppointmentForm } from '@/pages/Appointment/components/AppointmentDetailsModal/TransferAppointmentForm'
import { ZOCDOC_TRANSFER_BLOCKED } from '@/pages/Appointment/components/AppointmentDetailsModal/zocdocTransfer'
import {
  ActionButton,
  BADGE_TONE,
  CardBadge,
  CardSkeleton,
  DetailCard,
  type BadgeTone,
} from '@/components/ui/detailCardPrimitives'
import { KateCheckInCard } from './KateCheckInCard'
import type { AppointmentSearchResult } from './appointment-search'
import { useKateCheckIn } from '../hooks/useKateCheckIn'

/**
 * Everything an appointment block needs to render its actions menu and detail
 * preview. Bundled into one object so the custom views can pass it straight
 * through instead of threading a dozen callbacks.
 */
export interface AppointmentContext {
  isStuffMember: boolean
  /** Reassigning to another practitioner is care-coordinator only. */
  isCareCoordinator: boolean
  selectedPractitionerCount: number
  isSendingMagicLink: boolean
  insuranceVerdictMap?: Record<string, InsuranceVerdict>
  returningPatientMap?: Record<string, boolean>
  doctorNameById?: Record<string, string>
  onOpenCall: (encounterId: string) => void
  onReschedule: (appt: CalendarEvent) => void
  onReassign: (appt: CalendarEvent) => void
  onCancel: (encounterId: string) => void
  /**
   * True while the cancel confirmation is open. The preview must stay put behind
   * it — the confirmation restates the appointment, and dismissing the preview
   * that launched it would leave the user with nothing to go back to if they
   * decide to keep the appointment.
   */
  isCancelConfirmOpen?: boolean
  onStatusChange: (appointmentId: string, status: AppointmentStatusValue) => void
  onSendMagicLink: (encounterId: string) => void
  /** Mints the patient's current join link and copies it; nothing is sent (PD-1873). */
  onCopyMagicLink: (encounterId: string) => void
  /** Called after a clinic insurance decision is saved, so the schedule can
   *  refresh the strip's insurance icon (mirrors how status changes propagate). */
  onClinicDecisionChange?: () => void
  /** Called after an appointment is transferred to another organisation — it
   *  leaves this calendar, so the schedule reloads its encounters. */
  onAppointmentTransferred?: () => void
  /** Opens another visit on the schedule the way the appointment search does — the rebooking Kate made. */
  onJumpToAppointment?: (result: AppointmentSearchResult) => void
  highlightedEventId?: string | null
}

/**
 * Viewport from which the appointment preview grows to three columns. The grid's
 * `min-[1440px]:` classes below must stay literal (Tailwind only detects class
 * names written out in source), so keep this number and those in step.
 */
const WIDE_PREVIEW_QUERY = '(min-width: 1440px)'

const stopEvent = (e: React.MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()
}

// ── Appointment detail card (mockup redesign) ─────────────────────────────────
// Insurance, eligibility, consent, card images, co-pay, contact, clinic decision,
// and demographics (age/gender/DOB) are all loaded live (useAppointmentInsurance +
// VisitCoPayPanel), mirroring AppointmentDetailsModal's BL.
const DASH = '—'

const moneyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

// Exact dollar amount from cents (e.g. "$20.00") — for the co-pay card.

// Best-effort deductible read from the eligibility response's in/out-of-network
// deductible benefit items; returns undefined when the payer response omits them.
/**
 * The mark on a visit that came from Zocdoc.
 *
 * A lettermark rather than Zocdoc's own logo: this sits in a row of plain glyphs at 11px, where a
 * reproduced brand reads as decoration and would be the only third-party logo in the product. Black
 * on yellow is the one combination in this row that nothing else uses — the status icons are blue,
 * green and coral, the shield is grey — so it reads as a badge rather than as another state.
 *
 * The tooltip carries the Zocdoc appointment id, because an operator who wants to know more about
 * this booking would otherwise open the integration dashboard to find exactly that string.
 */
export function ZocdocChip({ id, big }: { id: string; big: boolean }) {
  return (
    <Tooltip label={`Booked on Zocdoc · ${id}`} withArrow>
      <span
        aria-label="Booked on Zocdoc"
        className={`inline-flex shrink-0 items-center justify-center rounded-full border border-[#D4A916] bg-[#FDE047] font-bold text-[#1F2733] ${
          big ? 'h-4 w-4 text-[10px]' : 'h-3.5 w-3.5 text-[9px]'
        }`}
      >
        {/* `leading-none` so flex centring centres the glyph rather than the font's line box.
            No nudge beyond that: measured in the browser, Plus Jakarta Sans puts the cap within
            0.15px of centre on its own, and every hand-tuned offset here made it worse. */}
        <span className="block leading-none">Z</span>
      </span>
    </Tooltip>
  )
}

/**
 * The self-pay fee is still owed (PD-1943): the care coordinator's cue to collect it or call the
 * patient, drawn on the strip next to the status so it shows without opening the preview.
 */
function UnpaidChip({ big }: { big: boolean }) {
  return (
    <Tooltip label="Self-pay fee unpaid" withArrow>
      <span
        className={`inline-flex shrink-0 items-center rounded-full px-1.5 leading-none font-bold ${BADGE_TONE.coral} ${
          big ? 'h-4 text-[10px]' : 'h-3.5 text-[9px]'
        }`}
      >
        Unpaid
      </span>
    </Tooltip>
  )
}

function formatDeductible(response: CoverageEligibilityResponse | null): string | undefined {
  const items = response?.insurance?.[0]?.item ?? []
  const deductibles = items.filter((item) =>
    item.category?.coding?.some((coding) => coding.code === 'deductible'),
  )
  if (!deductibles.length) return undefined
  const amountFor = (network: string): string | undefined => {
    const match = deductibles.find((item) =>
      item.network?.coding?.some((coding) => coding.code === network),
    )
    const value = match?.benefit?.[0]?.allowedMoney?.value
    return value == null ? undefined : moneyFmt.format(value)
  }
  const parts: string[] = []
  const inNetwork = amountFor('in')
  const outNetwork = amountFor('out')
  if (inNetwork) parts.push(`${inNetwork} in-network`)
  if (outNetwork) parts.push(`${outNetwork} out-of-network`)
  return parts.length ? parts.join(' · ') : undefined
}

// The payer-verified plan type rides on the eligibility response as a tag (the
// clearinghouse's X12 EB04 signal mapped to our vocabulary); absent when it proved nothing.
const PLAN_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(PLAN_TYPES_BY_CATEGORY).flatMap((planTypes) =>
    planTypes.map((planType) => [planType.value, planType.label]),
  ),
)

function formatPlanType(response: CoverageEligibilityResponse | null): string | undefined {
  const code = response?.meta?.tag?.find((tag) => tag.system === INSURANCE_PLAN_TYPE_SYSTEM)?.code
  return code ? (PLAN_TYPE_LABELS[code] ?? code) : undefined
}

/**
 * Single-value equivalent of {@link CardSkeleton} for values sitting inside a
 * line of text (the card header's name/gender/DOB), so a value that's still
 * loading reads as a shaded box like the cards' rows rather than a bare dash.
 * The wrapping span keeps Mantine's block-level Skeleton inline with the text.
 */
const InlineSkeleton = ({ width, height = 11 }: { width: number; height?: number }) => (
  <span className="inline-block align-middle">
    <Skeleton height={height} width={width} radius="sm" />
  </span>
)

const MetaRow = ({
  label,
  value,
  dotColor,
}: {
  label: string
  value: React.ReactNode
  dotColor?: string
}) => (
  <div className="flex items-center justify-between gap-3 py-[3px] text-[12.5px]">
    <span className="shrink-0 text-[#9AA0A8]">{label}</span>
    <span className="flex min-w-0 items-center gap-1.5 font-semibold text-[#1F2733]">
      {dotColor && (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: dotColor }} />
      )}
      <span className="truncate">{value}</span>
    </span>
  </div>
)

const FieldBox = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 rounded-lg border border-[#E7E9EC] px-2.5 py-[3px]">
    <div className="!text-[9px] font-semibold text-[#9AA0A8] uppercase">{label}</div>
    <div className="truncate text-[12px] font-semibold text-[#1F2733]">{value}</div>
  </div>
)

const ContactPill = ({
  icon,
  value,
  copyMessage,
}: {
  icon: React.ReactNode
  value: string
  /** Toaster shown after a successful copy; also gates the copy button's visibility. */
  copyMessage?: string
}) => {
  const canCopy = Boolean(copyMessage) && value !== DASH && value.trim() !== ''
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      showNotification({ color: 'green', message: copyMessage })
    } catch {
      showNotification({ color: 'red', message: 'Unable to copy to clipboard.' })
    }
  }
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#E7E9EC] px-2.5 py-1 text-[12px] text-[#33383F]">
      <span className="shrink-0 text-[#9AA0A8]">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{value}</span>
      {canCopy && (
        <button
          type="button"
          onClick={(e) => void handleCopy(e)}
          aria-label={copyMessage}
          className="shrink-0 text-[#9AA0A8] hover:text-[#2C5FE0]"
        >
          <IconCopy size={13} stroke={2} />
        </button>
      )}
    </div>
  )
}

const InsuranceCardThumb = ({
  title,
  caption,
  imageUrl,
  muted,
}: {
  title: string
  caption: string
  imageUrl: string | null
  muted?: boolean
}) => {
  const [zoomOpen, setZoomOpen] = useState(false)
  const stop = (e: React.MouseEvent) => e.stopPropagation()
  const openZoom = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (imageUrl) setZoomOpen(true)
  }
  const openInNewTab = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (imageUrl) window.open(imageUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          disabled={!imageUrl}
          onClick={openZoom}
          aria-label={imageUrl ? `Enlarge ${caption}` : `${caption} unavailable`}
          className={`mb-1 flex h-[74px] w-full items-center justify-center overflow-hidden rounded-lg border transition ${
            muted ? 'border-[#E7E9EC] bg-[#F7F8FA]' : 'border-[#CFE0F3] bg-[#EAF2FB]'
          } ${imageUrl ? 'cursor-zoom-in hover:border-[#2C5FE0]' : 'cursor-default'}`}
        >
          {imageUrl ? (
            <img src={imageUrl} alt={caption} className="h-full w-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-0.5">
              <span
                className={`text-[10px] font-bold ${muted ? 'text-[#8A94A6]' : 'text-[#2C5FE0]'}`}
              >
                {title}
              </span>
              <span className="text-[12px] font-semibold text-[#9AA0A8]">N/A</span>
            </div>
          )}
        </button>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-[#9AA0A8]">{imageUrl ? caption : ''}</span>
          <button
            type="button"
            disabled={!imageUrl}
            onClick={openInNewTab}
            className="!text-[12px] !font-bold text-[#2C5FE0] hover:underline disabled:text-[#B4B9C0] disabled:no-underline"
          >
            View
          </button>
        </div>
      </div>
      <Modal
        opened={zoomOpen}
        onClose={() => setZoomOpen(false)}
        title={caption}
        centered
        radius="lg"
        size="auto"
        zIndex={400}
      >
        <div
          onClick={stop}
          onMouseDown={stop}
          className="flex max-h-[78vh] max-w-[82vw] items-center justify-center overflow-auto p-2"
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt={caption}
              className="max-h-[74vh] max-w-[78vw] rounded-lg object-contain"
            />
          )}
        </div>
      </Modal>
    </>
  )
}

/** Full appointment detail card — the mockup layout shown when a block is pinned/hovered. */
const AppointmentPreview = ({
  appt,
  ctx,
  onNavigate,
}: {
  appt: CalendarEvent
  ctx: AppointmentContext
  /** Called before navigating away (e.g. to dismiss a pinned popover). */
  onNavigate?: () => void
}) => {
  // Live insurance, eligibility, consent, card images, contact, and clinic decision,
  // mirroring AppointmentDetailsModal's BL.
  const {
    patient,
    appointment,
    coverage,
    coverages,
    coverageResponse,
    cardImages,
    consentSigned,
    decision: orgDecision,
    loading: insLoading,
    refresh,
  } = useAppointmentInsurance(appt.id)
  // A self-pay visit (PD-1943): the Insurance card says so instead of offering a plan to
  // attach, and the clinic-decision, eligibility and card-image cards do not apply.
  const selfPay = appointment ? isSelfPayAppointment(appointment) : false
  const medplum = useMedplum()
  const navigate = useNavigate()
  const { isManager } = useIsManager()
  const kate = useKateCheckIn(appt.appointmentId, appt.appointmentStatus)
  const kateCardRef = useRef<HTMLDivElement>(null)
  const viewRebookedVisit = (visit: { encounterId?: string; start: string; end?: string }) => {
    if (!visit.encounterId || !ctx.onJumpToAppointment) return
    onNavigate?.()
    ctx.onJumpToAppointment({
      encounterId: visit.encounterId,
      patientId: patient?.id,
      patientName: appt.title,
      practitionerId: appt.practitionerId,
      start: visit.start,
      end: visit.end ?? visit.start,
    })
  }
  const [isChangingInsurance, setIsChangingInsurance] = useState(false)
  // Clinic-decision approve/disapprove flow (mirrors InsuranceOrgDecisionPanel).
  const [decisionMode, setDecisionMode] = useState<EligibilityDecisionMode | null>(null)
  const [eligReportOpen, setEligReportOpen] = useState(false)
  const [decisionSaving, setDecisionSaving] = useState(false)

  // Record a clinic-level insurance acceptance decision, then reload the preview —
  // same BL as the old AppointmentDetailsModal's InsuranceOrgDecisionPanel.
  const handleDecisionSubmit = async (values: { reason: string; notes: string }) => {
    if (!decisionMode || !coverage?.id || !patient?.id) return
    const profile = medplum.getProfile()
    if (!profile || profile.resourceType !== 'Practitioner') {
      showNotification({ color: 'red', message: 'A practitioner profile is required.' })
      return
    }
    const organizationId = await resolveCurrentOrganizationId(medplum, profile.id)
    if (!organizationId) {
      showNotification({ color: 'red', message: 'Unable to resolve the current organization.' })
      return
    }
    setDecisionSaving(true)
    try {
      await medplum.createResource(
        buildInsuranceOrgDecisionQuestionnaireResponse({
          patientId: patient.id,
          practitioner: profile,
          organizationId,
          coverage,
          mode: decisionMode,
          reason: values.reason,
          notes: values.notes,
          encounterId: appt.id,
        }),
      )
      setDecisionMode(null)
      refresh()
      // Refresh the schedule's clinic-decision map so the strip's insurance icon updates.
      ctx.onClinicDecisionChange?.()
      showNotification({
        color: 'green',
        message:
          decisionMode === 'approve'
            ? 'Insurance approved for this clinic.'
            : 'Insurance declined for this clinic.',
      })
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Unable to save insurance decision.',
      })
    } finally {
      setDecisionSaving(false)
    }
  }

  // Attach the chosen coverage to the appointment, then reload dependents — mirrors
  // AppointmentDetailsModal's handleAppointmentCoverageChange.
  const handleCoverageChange = async (coverageId: string | null) => {
    setIsChangingInsurance(false)
    if (!appointment) return
    const selected = coverages.find((item) => item.value === coverageId)?.coverage ?? null
    try {
      const supportingInformation = [
        ...(appointment.supportingInformation ?? []).filter(
          (ref) => !ref.reference?.startsWith('Coverage/'),
        ),
        ...(selected?.id ? [{ reference: `Coverage/${selected.id}` }] : []),
      ]
      await medplum.updateResource<Appointment>({
        ...appointment,
        supportingInformation: supportingInformation.length ? supportingInformation : undefined,
      })
      showNotification({
        color: 'green',
        message: selected ? 'Appointment insurance updated.' : 'Appointment insurance removed.',
      })
      refresh()
      // The new coverage may carry a different clinic decision — refresh the
      // schedule's map so the strip's insurance icon reflects it without a reload.
      ctx.onClinicDecisionChange?.()
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Failed to update appointment insurance.',
      })
    }
  }

  // ── Transfer to another organisation ───────────────────────────────────────
  // Reuses the proven TransferAppointmentForm (org connects -> slot -> transfer)
  // from the appointment details modal rather than reimplementing that flow.
  const [transferOpen, setTransferOpen] = useState(false)
  const [callerOrgId, setCallerOrgId] = useState<string | null>(null)
  const [resolvingOrg, setResolvingOrg] = useState(false)

  const openTransfer = async () => {
    const profile = medplum.getProfile()
    if (!profile || profile.resourceType !== 'Practitioner') {
      showNotification({ color: 'red', message: 'A practitioner profile is required.' })
      return
    }
    setResolvingOrg(true)
    try {
      const organizationId = await resolveCurrentOrganizationId(medplum, profile.id)
      if (!organizationId) {
        showNotification({ color: 'red', message: 'Unable to resolve the current organization.' })
        return
      }
      setCallerOrgId(organizationId)
      setTransferOpen(true)
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Unable to start the transfer.',
      })
    } finally {
      setResolvingOrg(false)
    }
  }

  // Once transferred the appointment belongs to the target org, so it drops off
  // this calendar — dismiss the popup and ask the schedule to reload.
  const handleTransferred = () => {
    setTransferOpen(false)
    showNotification({ color: 'green', message: 'Appointment transferred.' })
    ctx.onAppointmentTransferred?.()
    onNavigate?.()
  }

  const name = appt.title || 'Patient'
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?'
  const statusValue = (appt.appointmentStatus as string) || 'booked'
  const statusColor = APPOINTMENT_STATUS_CHIP[statusValue]?.color ?? '#16A34A'
  const statusLabel =
    APPOINTMENT_STATUS_OPTIONS.find((o) => o.value === statusValue)?.label ?? 'Confirmed'
  const doctorColor = appt.backgroundColor || '#316dab'
  // Doctor identity + dot depend on who's viewing:
  // - Staff (multi-doctor view): resolve the appointment's practitioner from the
  //   map, and show the doctor-colored dot to disambiguate whose column it is.
  //   Fall back to a neutral dash (never a hardcoded clinician) if unresolved.
  // - A doctor viewing their own schedule: it's always their appointment, so use
  //   their own name and drop the (redundant, single-color) dot.
  const ownName = medplum.getProfile()?.name?.[0]
    ? formatHumanName(medplum.getProfile()!.name![0])
    : undefined
  const doctorName = ctx.isStuffMember
    ? ((appt.practitionerId ? ctx.doctorNameById?.[appt.practitionerId] : undefined) ?? DASH)
    : (ownName ?? DASH)
  // Contact — off the patient resource (same expressions as AppointmentDetailsModal).
  // Strip the leading +1 US country code so the number displays (and copies) locally.
  const rawPhone = patient?.telecom?.find((item) => item.system === 'phone')?.value
  const phone = rawPhone ? rawPhone.replace(/^\+1[\s-]?/, '') : DASH
  const email = patient?.telecom?.find((item) => item.system === 'email')?.value ?? DASH

  // Demographics — off the patient resource (same expressions as AppointmentDetailsModal).
  // DOB/age prefer the birth date preloaded with the schedule load, so they're
  // filled the moment the preview opens rather than after the patient read.
  const birthDate = appt.patientBirthDate ?? patient?.birthDate
  // Same idea for the chart link: the id is known from the calendar event, so
  // "Open chart" is clickable before the patient read resolves.
  const patientId = appt.patientId ?? patient?.id
  // While the patient read is in flight these render as shaded boxes (matching the
  // cards' skeletons); the dash is kept for "loaded, but the field is empty".
  const age: React.ReactNode = birthDate ? (
    calculateAge(birthDate)
  ) : insLoading ? (
    <InlineSkeleton width={26} height={15} />
  ) : (
    DASH
  )
  const gender: React.ReactNode = patient?.gender ? (
    `${patient.gender.charAt(0).toUpperCase()}${patient.gender.slice(1)}`
  ) : insLoading ? (
    <InlineSkeleton width={44} />
  ) : (
    DASH
  )
  const dob: React.ReactNode = birthDate ? (
    formatDateTime(birthDate, '/', false, true)
  ) : insLoading ? (
    <InlineSkeleton width={62} />
  ) : (
    DASH
  )

  // The middleware's verdict decides the two badges (insuranceVerdicts); the raw decision and
  // eligibility response below only supply their details (reason, reviewer, plan, deductible).
  const verdict = ctx.insuranceVerdictMap?.[appt.id]
  const decisionLabel = clinicDecisionLabel(verdict?.clinicDecision ?? null)
  const decisionTone: BadgeTone =
    verdict?.clinicDecision === 'approve'
      ? 'green'
      : verdict?.clinicDecision === 'disapprove'
        ? 'coral'
        : 'gray'
  const decisionReason = orgDecision?.reason || DASH
  const decisionReviewedBy = orgDecision?.reviewedBy || DASH
  const decisionReviewedOn = orgDecision?.reviewedOn
    ? formatDateTime(orgDecision.reviewedOn, DASH, true, true)
    : DASH

  const start = new Date(appt.start)
  const apptDate = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${fmtTime(appt.start)}`
  const timeRange = `${fmtTime(appt.start)}–${fmtTime(appt.end)}`

  // ── Real insurance / eligibility (falls back to a dash while loading or absent) ──
  const planSnapshot = coverage ? getCoveragePlanSnapshot(coverage) : null
  const insName = coverage?.payor?.[0]?.display || (insLoading ? DASH : 'No coverage attached')
  const insType = coverage?.relationship?.coding?.[0]?.display ?? DASH
  const insPayerId = coverage?.payor?.[0]?.identifier?.value ?? DASH
  const insMemberId = coverage?.subscriberId ?? DASH
  const insProduct = planSnapshot?.productType || coverage?.type?.text || DASH

  const eligStatus = verdict ? PAYER_ELIGIBILITY_LABELS[verdict.payerEligibility] : DASH
  const eligColor = verdict ? payerEligibilityColor(verdict.payerEligibility) : 'gray'
  const eligTone: BadgeTone =
    eligColor === 'green' ? 'green' : eligColor === 'red' ? 'coral' : 'gray'
  const eligLastCheck = coverageResponse
    ? formatDateTime(
        coverageResponse.meta?.lastUpdated || coverageResponse.created || '',
        DASH,
        true,
      )
    : DASH
  const eligPlan =
    planSnapshot?.planName || (coverage ? getInsuranceDisplayData(coverage).plan : '') || DASH
  const eligDeductible = formatDeductible(coverageResponse) ?? DASH
  const eligPlanType = formatPlanType(coverageResponse) ?? DASH

  const consentLabel =
    consentSigned === true ? 'Consent: yes' : consentSigned === false ? 'Consent: no' : 'Consent: —'
  const consentTone: BadgeTone =
    consentSigned === true ? 'green' : consentSigned === false ? 'coral' : 'gray'

  // Wrap a ctx action so the pinned popover dismisses first (matches the old preview).
  const withClose = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    onNavigate?.()
    fn()
  }
  // Portaled popover clicks bubble through the React tree to the cell's slot handler;
  // stop them so mock/no-op controls don't open the create modal.
  const stop = (e: React.MouseEvent) => e.stopPropagation()

  // Cancel keeps the fourth slot in the action row for every role. Care
  // coordinators get an extra Reassign button, which would otherwise push
  // Cancel onto a second row and move the most destructive action depending on
  // who is looking — so for that role Transfer takes the wrapped slot instead.
  // Swapped in the markup rather than with CSS `order` so tab order still
  // follows what is on screen.
  // A Zocdoc booking names this organisation's provider, so moving the visit to another
  // organisation would leave the marketplace booking pointing at a practitioner who no longer
  // holds it. Blocked rather than hidden, so the reason is there to read.
  const transferBlocked = Boolean(appt.zocdocAppointmentId)
  const transferButton = (
    <Tooltip
      label={ZOCDOC_TRANSFER_BLOCKED}
      disabled={!transferBlocked}
      withArrow
      multiline
      w={240}
      // Above the preview popover this sits in.
      zIndex={400}
    >
      {/* A disabled button fires no mouse events, so the tooltip hangs off this wrapper. */}
      <span className="flex flex-1">
        <ActionButton
          icon={<IconBuildingHospital size={16} stroke={2} />}
          label={resolvingOrg ? 'Opening...' : 'Transfer to organisation'}
          disabled={transferBlocked}
          onClick={(e) => {
            e.stopPropagation()
            void openTransfer()
          }}
        />
      </span>
    </Tooltip>
  )
  // Mirrors the server's status and time gates (the server stays the authority): no link for a
  // visit that is over, none for one more than a week out — the link would die before it.
  const copyWindowEndsMs = Date.parse(appt.end) + 60 * 60_000
  const canCopyLink =
    appt.appointmentStatus !== 'noshow' &&
    appt.appointmentStatus !== 'fulfilled' &&
    !(copyWindowEndsMs < Date.now()) &&
    !(copyWindowEndsMs - Date.now() > 7 * 24 * 60 * 60_000)
  const cancelButton = (
    <ActionButton
      icon={<IconTrash size={16} stroke={2} />}
      label="Cancel"
      coral
      // Deliberately not `withClose`: the confirmation opens on top of this
      // preview, and the preview only goes away once the appointment is
      // actually cancelled (its strip unmounts with the reloaded list).
      onClick={(e) => {
        e.stopPropagation()
        ctx.onCancel(appt.id)
      }}
    />
  )

  return (
    // Catch-all: the dropdown is portaled, but React events still bubble through the
    // component tree to the calendar cell's slot handler behind it — stop every click
    // (and mousedown) here so interacting with the card never triggers the cell.
    <div
      className="flex flex-col gap-3.5"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EEF1FB] text-[13px] font-bold text-[#4263EB]">
          {initials}
        </span>
        <div className="min-w-0 flex-1 truncate text-[19px] font-extrabold tracking-tight text-[#1F2733]">
          {name}, {age}
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          {/* Doctor-colored dot — only for staff (multi-doctor view), matching
              the doctors selector's visibility condition on the schedule page. */}
          {ctx.isStuffMember && (
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: doctorColor }} />
          )}
          <button
            type="button"
            disabled={!patientId}
            onClick={withClose(() => {
              if (patientId) void navigate(`/patients/${patientId}`)
            })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E7E9EC] bg-white px-3 py-[3px] !text-[12px] !font-bold text-[#33383F] hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <IconFilePlus size={16} stroke={2} />
            Open chart
          </button>
          <button
            type="button"
            onClick={withClose(() => ctx.onOpenCall(appt.id))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#2C5FE0] px-3 py-[3px] !text-[12px] !font-bold text-white hover:bg-[#254FC0]"
          >
            <IconVideoFilled size={16} />
            Join video
          </button>
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation()
              onNavigate?.()
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E7E9EC] text-[#9AA0A8] hover:bg-[#F4F6F8]"
          >
            <IconX size={16} stroke={2.2} />
          </button>
        </div>
      </div>

      {/* Demographics get their own full-width row so the name row above has all
          the space it needs for the full name; the status selector sits at this
          row's right edge, keeping the crowded action row clear. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="text-[12.5px] font-medium text-[#6B7280]">
            {gender} · DOB {dob} · {timeRange}
          </div>
          {/* Visit reason (Encounter.reasonCode[0].text) — the row collapses away
              entirely when the encounter carries no reason. */}
          {appt.reasonLabel && (
            <span
              className={`w-fit rounded-md px-2 py-[3px] text-[11px] font-bold ${BADGE_TONE.coral}`}
            >
              Reason for visit: {appt.reasonLabel}
            </span>
          )}
          {/* The card has to answer the question the block's chip raises. An icon that says "this
              came from somewhere else" and a card that then says nothing about it is worse than no
              icon — so the origin is stated here. The Zocdoc id stays on the chip's tooltip. */}
          {appt.zocdocAppointmentId && (
            <span
              className={`w-fit rounded-md px-2 py-[3px] text-[11px] font-bold ${BADGE_TONE.coral}`}
            >
              Booked on Zocdoc
            </span>
          )}
          {appt.selfPayStatus === 'unpaid' && (
            <span
              className={`w-fit rounded-md px-2 py-[3px] text-[11px] font-bold ${BADGE_TONE.coral}`}
            >
              Unpaid
            </span>
          )}
        </div>
        <Menu withinPortal position="bottom-end">
          <Menu.Target>
            <button
              type="button"
              onClick={stop}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-[3px] !text-[12px] !font-bold"
              style={{ background: `${statusColor}1f`, color: statusColor }}
            >
              {statusLabel}
              <IconChevronDown size={14} stroke={2.4} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            {APPOINTMENT_STATUS_OPTIONS.filter(
              ({ value }) => value !== 'checked-in' || isManager,
            ).map(({ value, label }) => (
              <Menu.Item
                key={value}
                disabled={!appt.appointmentId}
                fw={statusValue === value ? 700 : undefined}
                onClick={(e) => {
                  stopEvent(e)
                  ctx.onStatusChange(appt.appointmentId as string, value)
                }}
              >
                {label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </div>

      <div className="h-px bg-[#EEF0F2]" />

      <div className="flex flex-wrap gap-2">
        <ActionButton
          icon={<IconCalendarEvent size={16} stroke={2} />}
          label="Reschedule"
          onClick={withClose(() => ctx.onReschedule(appt))}
        />
        <ActionButton
          icon={<IconSend size={16} stroke={2} />}
          label="Resend link"
          onClick={withClose(() => ctx.onSendMagicLink(appt.id))}
        />
        <ActionButton
          icon={<IconCopy size={16} stroke={2} />}
          label="Copy link"
          disabled={!canCopyLink}
          onClick={withClose(() => ctx.onCopyMagicLink(appt.id))}
        />
        {kate.data?.rebooking ? (
          <ActionButton
            icon={<IconSparkles size={16} stroke={2} />}
            label={
              kate.busy === 'send'
                ? 'Sending…'
                : kate.data.rebooking.invitedAt
                  ? 'View Kate follow-up'
                  : 'Send Kate follow-up'
            }
            disabled={kate.busy !== null}
            onClick={(e) => {
              e.stopPropagation()
              // Once an invite is out, a resend is the card's explicit "Resend".
              if (kate.data?.rebooking?.invitedAt) {
                kateCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
              } else {
                void kate.send('rebooking')
              }
            }}
          />
        ) : (
          <ActionButton
            icon={<IconSparkles size={16} stroke={2} />}
            label={kate.busy === 'send' ? 'Sending…' : 'Send Kate check-in'}
            disabled={kate.busy !== null}
            onClick={(e) => {
              e.stopPropagation()
              void kate.send()
            }}
          />
        )}
        {ctx.isCareCoordinator && (
          <ActionButton
            icon={<IconArrowsExchange size={16} stroke={2} />}
            label="Reassign Appointment"
            onClick={withClose(() => ctx.onReassign(appt))}
          />
        )}
        {ctx.isCareCoordinator ? cancelButton : transferButton}
        {ctx.isCareCoordinator ? transferButton : cancelButton}
      </div>

      {/* Kate sits in a third column on wide screens and first on laptops, so the
          check-in is visible without scrolling either way. */}
      <div className="grid grid-cols-2 gap-3 min-[1440px]:grid-cols-3">
        <DetailCard
          icon={<IconUser size={17} stroke={2} />}
          title="Patient & contact"
          subtitle="Information needed for the visit"
          badge={insLoading ? undefined : <CardBadge label={consentLabel} tone={consentTone} />}
        >
          {insLoading ? (
            <div className="mb-2.5 flex flex-col gap-2">
              <Skeleton height={28} radius="md" />
              <Skeleton height={28} radius="md" />
            </div>
          ) : (
            <div className="mb-2.5 flex flex-col gap-2">
              <ContactPill
                icon={<IconPhone size={14} stroke={2} />}
                value={phone}
                copyMessage="Phone number copied to clipboard"
              />
              <ContactPill
                icon={<IconMail size={14} stroke={2} />}
                value={email}
                copyMessage="Email copied to clipboard"
              />
            </div>
          )}
          <MetaRow
            label="Provider"
            value={doctorName}
            dotColor={ctx.isStuffMember ? doctorColor : undefined}
          />
          <MetaRow label="Appointment" value={apptDate} />
        </DetailCard>

        <DetailCard
          icon={<IconWallet size={17} stroke={2} />}
          title="Insurance"
          subtitle="Attached coverage"
          action={
            selfPay ? undefined : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsChangingInsurance((open) => !open)
                }}
                className="shrink-0 rounded-lg border border-[#D8DBE0] px-2.5 py-1 !text-[12px] !font-bold text-[#33383F] hover:bg-[#F4F6F8]"
              >
                {isChangingInsurance ? 'Cancel' : coverage ? 'Change' : 'Assign'}
              </button>
            )
          }
        >
          {selfPay ? (
            <div className="mb-2.5 text-[13px] font-bold text-[#1F2733]">
              Self Pay · No insurance used for this visit
            </div>
          ) : isChangingInsurance ? (
            <div onClick={stop}>
              <Select
                data={coverages.map((item) => ({ value: item.value, label: item.label }))}
                value={coverage?.id ?? null}
                onChange={(value) => void handleCoverageChange(value)}
                placeholder="Select appointment insurance"
                radius="md"
                comboboxProps={{ withinPortal: false }}
                nothingFoundMessage="No coverages found"
                clearable
              />
            </div>
          ) : insLoading ? (
            <>
              <Skeleton height={16} width="55%" radius="sm" className="mb-2.5" />
              <div className="grid grid-cols-2 gap-1.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={40} radius="md" />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="mb-2.5 text-[13px] font-bold text-[#1F2733]">{insName}</div>
              <div className="grid grid-cols-2 gap-1.5">
                <FieldBox label="Type" value={insType} />
                <FieldBox label="Payer ID" value={insPayerId} />
                <FieldBox label="Member ID" value={insMemberId} />
                <FieldBox label="Coverage" value={insProduct} />
              </div>
            </>
          )}
        </DetailCard>

        {!selfPay && (
          <>
            <DetailCard
              icon={<IconBuildingHospital size={17} stroke={2} />}
              title="Clinic decision"
              subtitle="Insurance routing review"
              badge={
                insLoading ? undefined : <CardBadge label={decisionLabel} tone={decisionTone} />
              }
            >
              {insLoading ? (
                <CardSkeleton />
              ) : (
                <>
                  <MetaRow label="Reason" value={decisionReason} />
                  <MetaRow label="Reviewed by" value={decisionReviewedBy} />
                  <MetaRow label="Reviewed on" value={decisionReviewedOn} />
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      disabled={!coverage || orgDecision?.mode === 'approve'}
                      onClick={(e) => {
                        e.stopPropagation()
                        setDecisionMode('approve')
                      }}
                      className="inline-flex flex-1 items-center justify-center rounded-lg border border-[#BFE3CC] bg-white px-2.5 py-[3px] !text-[12px] !font-bold text-[#1A8245] hover:bg-[#F1FAF4] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={!coverage || orgDecision?.mode === 'disapprove'}
                      onClick={(e) => {
                        e.stopPropagation()
                        setDecisionMode('disapprove')
                      }}
                      className="inline-flex flex-1 items-center justify-center rounded-lg border border-[#F3C9C0] bg-white px-2.5 py-[3px] !text-[12px] !font-bold text-[#E5533D] hover:bg-[#FDF3F0] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Disapprove
                    </button>
                  </div>
                  <EligibilityDecisionModal
                    mode={decisionMode ?? 'approve'}
                    opened={Boolean(decisionMode)}
                    onClose={() => setDecisionMode(null)}
                    onSubmit={handleDecisionSubmit}
                    loading={decisionSaving}
                    context="organization"
                    // Sit above the preview popover (default z-index 300) that opened it.
                    zIndex={400}
                  />
                </>
              )}
            </DetailCard>

            <DetailCard
              icon={<IconShieldCheck size={17} stroke={2} />}
              title="Eligibility"
              subtitle="Last payer response"
              badge={insLoading ? undefined : <CardBadge label={eligStatus} tone={eligTone} />}
            >
              {insLoading ? (
                <CardSkeleton />
              ) : (
                <>
                  <MetaRow label="Last check" value={eligLastCheck} />
                  <MetaRow label="Plan" value={eligPlan} />
                  <MetaRow label="Plan type" value={eligPlanType} />
                  <MetaRow label="Deductible" value={eligDeductible} />
                  {coverageResponse && (
                    <div className="mt-2 flex">
                      <ActionButton
                        icon={<IconFileDescription size={14} stroke={2} />}
                        label="View more details"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEligReportOpen(true)
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </DetailCard>

            {/* The full payer report — the same panel the patient's Demographics shows. Above the
                preview popover (zIndex 400) and shielded from it, like the co-pay QR modal. */}
            <Modal
              opened={eligReportOpen}
              onClose={() => setEligReportOpen(false)}
              title="Eligibility report"
              centered
              size="lg"
              radius="lg"
              zIndex={400}
            >
              <div onClick={stop} onMouseDown={stop}>
                <CoverageEligibilityOutcomePanel
                  latestEligibilityResponse={coverageResponse}
                  className="max-h-[70vh] overflow-y-auto rounded-2xl border"
                />
              </div>
            </Modal>

            <DetailCard
              icon={<IconCreditCard size={17} stroke={2} />}
              title="Insurance card images"
              subtitle="Click a card image to enlarge and review member details."
            >
              {insLoading ? (
                <div className="flex gap-2.5">
                  <Skeleton height={74} radius="md" className="flex-1" />
                  <Skeleton height={74} radius="md" className="flex-1" />
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  <div className="flex gap-2.5">
                    <InsuranceCardThumb
                      title="Front side"
                      caption="Front of card"
                      imageUrl={cardImages.front}
                    />
                    <InsuranceCardThumb
                      title="Back side"
                      caption="Back of card"
                      imageUrl={cardImages.back}
                      muted
                    />
                  </div>
                </div>
              )}
            </DetailCard>
          </>
        )}

        <div onClick={(e) => e.stopPropagation()}>
          <VisitCoPayPanel encounterId={appt.id} selfPay={selfPay} />
        </div>

        <div
          ref={kateCardRef}
          className="order-first col-span-2 min-[1440px]:order-none min-[1440px]:col-span-1 min-[1440px]:col-start-3 min-[1440px]:row-span-3 min-[1440px]:row-start-1"
        >
          <KateCheckInCard
            checkIn={kate}
            providerName={ctx.doctorNameById?.[appt.practitionerId ?? '']}
            onViewAppointment={viewRebookedVisit}
          />
        </div>
      </div>

      <Modal
        opened={transferOpen}
        onClose={() => setTransferOpen(false)}
        title="Transfer to organisation"
        centered
        radius="lg"
        size="lg"
        // Sit above the preview popover (default z-index 300) that opened it.
        zIndex={400}
      >
        {appointment && patient && callerOrgId ? (
          <div onClick={stop} onMouseDown={stop}>
            <TransferAppointmentForm
              appointment={appointment}
              patient={patient}
              callerOrgId={callerOrgId}
              onClose={() => setTransferOpen(false)}
              onTransferred={handleTransferred}
            />
          </div>
        ) : (
          <div className="px-1 py-2 text-[13px] text-[#6B7280]">
            Still loading this appointment&apos;s details — try again in a moment.
          </div>
        )}
      </Modal>
    </div>
  )
}

/**
 * Compact white event strip — the primary card used across the calendar (week
 * cells, month cells, and the "+N" overflow popovers). A left doctor-colored
 * border, the patient title, status/insurance icons and the start time; clicking
 * it opens the rich detail preview. `preview` is the shared
 * {@link usePreviewPopover} state so only one preview is open per view.
 */
export const EventStrip = ({
  appt,
  ctx,
  preview,
  previewPosition = 'right-start',
  previewWithinPortal = true,
  big = false,
}: {
  appt: CalendarEvent
  ctx: AppointmentContext
  preview: PreviewPopover
  previewPosition?: FloatingPosition
  /** Desktop-Mac density (see `useBigScreen`): the calling grid has given the
   *  strip a taller row, so its text and icons scale up to match. */
  big?: boolean
  // Portal the preview to <body> by default. Set false when the strip lives
  // inside a "+N" hover dropdown: a portaled preview sits outside that
  // dropdown's DOM, so entering it fires the dropdown's mouseleave and the
  // parent HoverCard's close timer unmounts the strip mid-interaction. Keeping
  // the preview inside the dropdown makes it a descendant, so hover is retained.
  previewWithinPortal?: boolean
}) => {
  const widePreview = useMediaQuery(WIDE_PREVIEW_QUERY, false, { getInitialValueInEffect: false })
  const { openId, open, close } = preview
  // Cursor point where the preview was opened. The popover anchors to this
  // instead of the (often narrow, edge-hugging) event strip so the 720px card
  // opens from where the user is pointing and can't be clipped off-screen.
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null)
  const isPatientReady = appt.appointmentStatus === 'checked-in'
  const doctorColor = appt.backgroundColor || '#316dab'
  const status = APPOINTMENT_STATUS_CHIP[appt.appointmentStatus as string]
  // Status shown as an icon (legacy mapping) with the full label in a tooltip.
  const StatusIcon = APPOINTMENT_STATUS_ICON[appt.appointmentStatus as string]
  const statusLabel =
    APPOINTMENT_STATUS_OPTIONS.find((o) => o.value === appt.appointmentStatus)?.label ??
    status?.label
  // Insurance shown as a shield icon to save card space, from the insurance verdict: green
  // check = verified, amber exclamation = review required, grey = no insurance (or not loaded
  // yet). The tooltip carries the full text.
  const insuranceStatus = ctx.insuranceVerdictMap?.[appt.id]?.insuranceStatus
  const insChip = (insuranceStatus ? INSURANCE_STATUS_CHIP[insuranceStatus] : undefined) ?? {
    color: '#94A3B8',
    label: 'Insurance: no data',
  }
  const InsMark =
    insuranceStatus === 'verified'
      ? IconCheck
      : insuranceStatus === 'review-required'
        ? IconExclamationMark
        : null
  const dimmed = openId != null && openId !== appt.id
  // Dimmed cards stay opaque so any availability fill behind them never bleeds
  // through — dim via element-level opacity on a solid white background.
  const opacity = dimmed ? 0.55 : 1
  const previewOpen = openId === appt.id
  const highlighted = ctx.highlightedEventId === appt.id
  // Snapshot the cursor point when a preview is opened so the popover anchors there.
  const captureAnchor = (e: React.MouseEvent) => setAnchor({ x: e.clientX, y: e.clientY })
  const stripRef = useRef<HTMLDivElement>(null)
  // An open without a click (the schedule deep link) has no cursor point. Left at
  // (0,0), the zero-size target reads as detached to the popover's hideDetached
  // and the dropdown renders hidden, so anchor it to the strip's left edge.
  useLayoutEffect(() => {
    if (!previewOpen) {
      setAnchor(null)
      return
    }
    if (anchor) return
    const rect = stripRef.current?.getBoundingClientRect()
    if (!rect) return
    setAnchor({ x: rect.left + Math.min(24, rect.width / 2), y: rect.top + rect.height / 2 })
  }, [previewOpen, anchor])
  return (
    <>
      <div
        ref={stripRef}
        data-appointment-id={appt.id}
        // Mantine's click-outside runs on mousedown and treats this strip as
        // "outside" (the popover's target is the cursor anchor, not the strip).
        // Letting it through would close the preview on mousedown and the click
        // below would immediately reopen it, so the strip would never toggle shut.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          // Click toggles: same card closes, another card switches to it.
          if (previewOpen) {
            close()
            return
          }
          captureAnchor(e)
          open(appt.id)
        }}
        className="flex h-full min-w-0 cursor-pointer flex-col justify-center overflow-hidden rounded-md border border-[#E7E9EE] py-0 pr-[4px] pl-[2px]"
        style={{
          borderLeft: `8px solid ${doctorColor}`,
          background: '#fff',
          color: '#1F2733',
          opacity,
          boxShadow: highlighted
            ? '0 0 0 2px #E5533D, 0 8px 22px rgba(229,83,61,.35)'
            : previewOpen
              ? '0 8px 22px rgba(16,30,60,.18)'
              : '0 1px 2px rgba(16,30,60,.10)',
          transition: 'opacity .15s ease, box-shadow .15s ease',
        }}
      >
        <div className="flex min-w-0 items-center gap-1">
          <span
            className={`ml-2 min-w-0 flex-1 truncate font-bold ${big ? 'text-[12.5px]' : 'text-[10.5px]'}`}
          >
            {appt.title}
          </span>
          {isPatientReady && (
            <Tooltip label="Patient Ready" withArrow>
              <span className="shrink-0 rounded-sm bg-[#16A34A]/[0.12] px-1 py-px text-[9px] font-bold text-[#16A34A]">
                Ready
              </span>
            </Tooltip>
          )}
          {ctx.returningPatientMap?.[appt.id] && (
            <Tooltip label="Returning patient" withArrow>
              <IconHistory size={big ? 13 : 11} className="shrink-0 text-[#9AA0A8]" />
            </Tooltip>
          )}
          {StatusIcon && status && (
            <Tooltip label={statusLabel} withArrow>
              <StatusIcon
                size={big ? 14 : 12}
                stroke={2}
                color={status.color}
                className="shrink-0"
              />
            </Tooltip>
          )}
          {appt.zocdocAppointmentId && <ZocdocChip id={appt.zocdocAppointmentId} big={big} />}
          {appt.selfPayStatus === 'unpaid' && <UnpaidChip big={big} />}
          <Tooltip label={insChip.label} withArrow>
            <span
              className={`relative inline-flex shrink-0 items-center justify-center ${big ? 'h-4 w-4' : 'h-3.5 w-3.5'}`}
            >
              <IconShieldFilled
                size={big ? 16 : 14}
                color={insChip.color}
                className="absolute inset-0"
              />
              {InsMark && (
                <InsMark size={big ? 8 : 7} stroke={4} color="#fff" className="relative -mt-px" />
              )}
            </span>
          </Tooltip>
          <span
            className={`shrink-0 font-semibold text-[#8A8F98] ${big ? 'text-[11px]' : 'text-[9.5px]'}`}
          >
            {fmtTime(appt.start)}
          </span>
        </div>
      </div>
      <Popover
        opened={previewOpen}
        position={previewPosition}
        positionDependencies={[anchor]}
        width={widePreview ? 1100 : 720}
        withArrow
        radius={16}
        withinPortal={previewWithinPortal}
        // flip to the opposite side and shift along the viewport so the card is
        // never clipped on smaller screens; cap to the viewport and scroll when it
        // still can't fit. crossAxis lets the wide card slide over the cursor
        // anchor when neither side of it has 1100px free.
        middlewares={{ flip: true, shift: { crossAxis: true } }}
        // A confirmation dialog portals outside this dropdown, so every click in
        // it — including "Keep appointment" — reads as an outside click. Both
        // dismissals are suspended while one is open.
        closeOnClickOutside={!ctx.isCancelConfirmOpen}
        closeOnEscape={!ctx.isCancelConfirmOpen}
        styles={{
          dropdown: {
            padding: 18,
            border: '1px solid #E4E7EB',
            boxShadow: '0 14px 36px rgba(16,24,40,.18)',
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'calc(100vh - 24px)',
            overflowY: 'auto',
          },
        }}
        onChange={(o) => {
          if (!o && !ctx.isCancelConfirmOpen) close()
        }}
      >
        {/* Zero-size anchor pinned to the cursor point so the preview opens from
            where the user pointed rather than from the event strip's edge. */}
        <Popover.Target>
          <div
            style={{
              position: 'fixed',
              left: anchor?.x ?? 0,
              top: anchor?.y ?? 0,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
        </Popover.Target>
        <Popover.Dropdown>
          <AppointmentPreview appt={appt} ctx={ctx} onNavigate={close} />
        </Popover.Dropdown>
      </Popover>
    </>
  )
}
